<script setup lang="ts">
import { computed, onActivated, onBeforeUnmount, onDeactivated, ref, shallowRef, watch } from 'vue'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import type { Props } from './type'

defineOptions({
  name: 'DistributeFeedViewportItem',
})

const props = defineProps<Props>()
const runtimeBudget = getRuntimePerformanceBudget()

const rootElement = ref<HTMLDivElement | null>(null)
const measuredHeight = shallowRef(props.estimatedHeight)
const { isVisible } = useElementVisibility(rootElement, {
  initialVisible: false,
  rootMargin: runtimeBudget.feedItemRenderRootMargin,
  threshold: 0,
})
let resizeObserver: ResizeObserver | null = null
let isComponentActive = true

const shouldRenderContent = computed(() => isVisible.value)
const stableHeight = computed(() => Math.max(1, measuredHeight.value || props.estimatedHeight))
const itemStyle = computed(() => ({
  '--feed-viewport-item-height': `${stableHeight.value}px`,
}))

function stopMeasuring() {
  resizeObserver?.disconnect()
  resizeObserver = null
}

function startMeasuring() {
  stopMeasuring()

  if (!isComponentActive || !rootElement.value || typeof ResizeObserver === 'undefined') {
    return
  }

  resizeObserver = new ResizeObserver(([entry]) => {
    if (!isVisible.value) {
      return
    }

    const nextHeight = entry?.contentRect.height ?? 0

    if (nextHeight > 1) {
      measuredHeight.value = nextHeight
    }
  })
  resizeObserver.observe(rootElement.value)
}

watch(
  () => props.estimatedHeight,
  (nextHeight) => {
    if (!measuredHeight.value || Math.abs(measuredHeight.value - nextHeight) > 160) {
      measuredHeight.value = nextHeight
    }
  },
)

watch(rootElement, startMeasuring, { flush: 'post' })

onActivated(() => {
  isComponentActive = true
  startMeasuring()
})

onDeactivated(() => {
  isComponentActive = false
  stopMeasuring()
})

onBeforeUnmount(() => {
  isComponentActive = false
  stopMeasuring()
})
</script>

<template>
  <div ref="rootElement" class="feed-viewport-item" :style="itemStyle">
    <slot v-if="shouldRenderContent" />
    <slot v-else name="placeholder" />
  </div>
</template>

<style scoped>
.feed-viewport-item {
  width: 100%;
  min-height: var(--feed-viewport-item-height);
  contain: layout style;
}
</style>
